import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { UpdateAdminProfile } from '../../Redux/Actions/UsersProfileAction';
import { toast } from 'react-toastify';

const AdminProfile = () => {
    const dispatch = useDispatch();
    const UsersProfile = useSelector((state) => state.UsersProfile || {});
    const [profileData, setProfileData] = useState(null);
    const [selectedImage, setSelectedImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [formData, setFormData] = useState({
        firstname: '',
        lastname: '',
        email: '',
    });
    const [originalFormData, setOriginalFormData] = useState({
        firstname: '',
        lastname: '',
        email: '',
    });
    const [isLoading, setIsLoading] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);

    useEffect(() => {
        if (UsersProfile?.personalDetails?.data) {
            const data = UsersProfile.personalDetails.data;
            setProfileData(data);
            const newFormData = {
                firstname: data.firstname || '',
                lastname: data.lastname || '',
                email: data.email || '',
            };
            setFormData(newFormData);
            setOriginalFormData(newFormData);
            if (data.images?.[0]) {
                setImagePreview(data.images[0]);
            }
        }
    }, [UsersProfile]);

    // Track if form has changes (excluding email from changes check)
    useEffect(() => {
        const { email: _, ...formDataWithoutEmail } = formData;
        const { email: __, ...originalFormDataWithoutEmail } = originalFormData;

        const formChanged =
            JSON.stringify(formDataWithoutEmail) !== JSON.stringify(originalFormDataWithoutEmail) ||
            selectedImage !== null;

        setHasChanges(formChanged);
    }, [formData, selectedImage, originalFormData]);

    const validateImageFile = useCallback((file) => {
        const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
        if (!validTypes.includes(file.type)) {
            toast.error('Invalid file type. Please select a JPEG, JPG, PNG, or GIF image.');
            return false;
        }

        if (file.size > 5 * 1024 * 1024) {
            toast.error('Image size should not exceed 5MB.');
            return false;
        }

        return true;
    }, []);

    const handleImageChange = useCallback((event) => {
        const file = event.target.files?.[0];
        if (file && validateImageFile(file)) {
            setSelectedImage(file);
            const previewUrl = URL.createObjectURL(file);
            setImagePreview(previewUrl);
        }
    }, [validateImageFile]);

    const handleInputChange = useCallback((e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value.trim()
        }));
    }, []);

    const validateFormData = useCallback(() => {
        if (!formData.firstname.trim()) {
            toast.error('First name is required.');
            return false;
        }
        if (!formData.lastname.trim()) {
            toast.error('Last name is required.');
            return false;
        }
        // Email validation removed since it's not editable
        return true;
    }, [formData]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!profileData?.id) {
            toast.error('Profile data not available.');
            return;
        }

        if (!validateFormData()) {
            return;
        }

        if (!hasChanges) {
            toast.info('No changes to save.');
            return;
        }

        setIsLoading(true);

        try {
            const updateData = new FormData();
            updateData.append('firstname', formData.firstname);
            updateData.append('lastname', formData.lastname);
            updateData.append('email', formData.email); // Add this line

            if (selectedImage) {
                updateData.append('image', selectedImage);
            }

            // Debug: Log FormData contents
            console.log('FormData contents:');
            for (let [key, value] of updateData.entries()) {
                console.log(key, value);
            }

            await dispatch(UpdateAdminProfile(profileData.id, updateData));

            setSelectedImage(null);
            setOriginalFormData(formData);
            setIsEditing(false);
            toast.success('Profile updated successfully.');

        } catch (error) {
            console.error('Error updating profile:', error);
            toast.error(error?.response?.data?.message || 'Failed to update profile.');
        } finally {
            setIsLoading(false);
        }
    };
    const handleCancel = useCallback(() => {
        if (profileData) {
            setFormData(originalFormData);
            setImagePreview(profileData.images?.[0] || null);
            setSelectedImage(null);
        }
        setIsEditing(false);
    }, [profileData, originalFormData]);

    const handleRemoveImage = useCallback(() => {
        setSelectedImage(null);
        setImagePreview(profileData?.images?.[0] || null);
    }, [profileData]);

    // Cleanup image URLs to prevent memory leaks
    useEffect(() => {
        return () => {
            if (imagePreview?.startsWith('blob:')) {
                URL.revokeObjectURL(imagePreview);
            }
        };
    }, [imagePreview]);

    if (!profileData) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading admin profile...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 py-8">
            <div className="max-w-screen-xl mx-auto  px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                    <div className="flex justify-between items-center">
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900">Admin Profile</h1>
                            <p className="text-gray-600 mt-2">Manage your administrator account information</p>
                        </div>
                        <button
                            type="button"
                            onClick={() => setIsEditing(!isEditing)}
                            className="px-4 py-2 bg-[#e72848] text-white rounded-lg hover:bg-[#d62040] transition-colors"
                        >
                            {isEditing ? 'Cancel Edit' : 'Edit Profile'}
                        </button>
                    </div>
                </div>

                {/* Profile Form */}
                <form onSubmit={handleSubmit}>
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <div className="space-y-6">
                            {/* Profile Image Section */}
                            <div className="flex flex-col items-center space-y-4">
                                <div className="relative">
                                    <img
                                        src={imagePreview || "/images/default-avatar.png"}
                                        alt="Admin Profile Avatar"
                                        className="w-32 h-32 rounded-full object-cover border-4 border-gray-200"
                                        onError={(e) => {
                                            e.target.src = "/images/default-avatar.png";
                                        }}
                                    />
                                    {selectedImage && (
                                        <div className="absolute inset-0 bg-black bg-opacity-50 rounded-full flex items-center justify-center">
                                            <span className="text-white text-sm font-medium">New Image</span>
                                        </div>
                                    )}
                                </div>

                                {isEditing && (
                                    <div className="flex space-x-3">
                                        <label className="cursor-pointer bg-[#e72848] text-white px-4 py-2 rounded-lg hover:bg-[#d62040] transition-colors">
                                            <span>Change Image</span>
                                            <input
                                                type="file"
                                                accept="image/*"
                                                onChange={handleImageChange}
                                                className="hidden"
                                                aria-label="Upload profile image"
                                            />
                                        </label>

                                        {selectedImage && (
                                            <button
                                                type="button"
                                                onClick={handleRemoveImage}
                                                className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors"
                                                aria-label="Remove selected image"
                                            >
                                                Cancel
                                            </button>
                                        )}
                                    </div>
                                )}

                                <p className="text-sm text-gray-500 text-center">
                                    Supported formats: JPG, PNG, GIF. Max size: 5MB
                                </p>
                            </div>

                            {/* Profile Information */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {/* First Name */}
                                <div>
                                    <label htmlFor="firstname" className="block text-sm font-medium text-gray-700 mb-2">
                                        First Name
                                    </label>
                                    {isEditing ? (
                                        <input
                                            id="firstname"
                                            type="text"
                                            name="firstname"
                                            value={formData.firstname}
                                            onChange={handleInputChange}
                                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            required
                                            minLength="1"
                                            maxLength="50"
                                        />
                                    ) : (
                                        <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                                            <p className="text-gray-900">{formData.firstname || 'Not provided'}</p>
                                        </div>
                                    )}
                                </div>

                                {/* Last Name */}
                                <div>
                                    <label htmlFor="lastname" className="block text-sm font-medium text-gray-700 mb-2">
                                        Last Name
                                    </label>
                                    {isEditing ? (
                                        <input
                                            id="lastname"
                                            type="text"
                                            name="lastname"
                                            value={formData.lastname}
                                            onChange={handleInputChange}
                                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            required
                                            minLength="1"
                                            maxLength="50"
                                        />
                                    ) : (
                                        <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                                            <p className="text-gray-900">{formData.lastname || 'Not provided'}</p>
                                        </div>
                                    )}
                                </div>

                                {/* Email - Always Read-only */}
                                <div className="md:col-span-2">
                                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                                        Email Address
                                    </label>
                                    <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                                        <p className="text-gray-900">{formData.email}</p>
                                    </div>
                                    {isEditing && (
                                        <p className="text-sm text-gray-500 mt-1">
                                            Email address cannot be changed for security reasons.
                                        </p>
                                    )}
                                </div>
                            </div>

                            {/* Admin Badge */}
                            <div className="flex justify-center">
                                <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-red-100 text-red-800 border border-red-200">
                                    Administrator Account
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Action Buttons */}
                    {isEditing && (
                        <div className="flex justify-end space-x-4 mt-6">
                            <button
                                type="button"
                                onClick={handleCancel}
                                disabled={isLoading}
                                className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={isLoading || !hasChanges}
                                className="px-6 py-2 bg-[#e72848] text-white rounded-lg hover:bg-[#d62040] transition-colors disabled:opacity-50 flex items-center space-x-2"
                            >
                                {isLoading ? (
                                    <>
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                        <span>Saving...</span>
                                    </>
                                ) : (
                                    <span>Save Changes</span>
                                )}
                            </button>
                        </div>
                    )}
                </form>
            </div>
        </div>
    );
};

export default AdminProfile;